import { PlusCircleIcon } from "@phosphor-icons/react/PlusCircle";
import { WarningCircleIcon } from "@phosphor-icons/react/WarningCircle";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import type { Patch } from "@freightsimple/shared/types";
import type { UUID } from "@/Shared/types";

import BookShipmentAdditionalChargesQuestionBubble from "../Bubbles/BookShipmentAdditionalChargesQuestionBubble";
import LineItemsContainer from "@/Shared/LineItems/CustomComponents/LineItemsContainer";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { LineItemErrors } from "@/Shared/LineItems/Validators/lineItemsErrorMessages";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import { totalWeightForLineItems } from "@/Helpers/lineItemCalculations";
import { Units } from "@freightsimple/ui-core/Data/Units";
import { useState } from "react";
import BookShipmentSection from "./BookShipmentSection";
import { BookShipmentSelectInitialSavedCommodity } from "./BookShipmentSelectInitialSavedCommodity";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { LineItemsLimitReachedNote } from "@/Shared/LineItems/CustomComponents/LineItemsLimitReachedNote";

interface MultipleLineItemsContainerProps {
  lineItems: Array<AddLineItemType>;
  forceValidation: boolean;
  errorMessages: Map<string, LineItemErrors>;
  overallErrorMessage: ErrorMessageType;
  onUpdateLineItem: (lineItemId: UUID, value: Patch<LineItem>) => void;
  addAnotherLineItem: () => void;
  removeLineItemById: (lineItemId: UUID) => void;
  shouldRenderFreightClass: boolean;
  setHasNmfcError: (lineItemId: UUID, hasNmfcError: boolean) => void;
  savedCommodities: Array<SavedCommodity>;
  addLineItemFromSavedCommodity: (_: SavedCommodity) => void;
  duplicateLineItem: (lineItemId: UUID) => void;
  onRearrangeLengthWidth: (lineItemId: UUID) => void;
  pickupLocation: AddLocationLocationType;
  deliveryLocation: AddLocationLocationType;
  protectFromFreezingOutOfSeason: boolean;
  maxLineItems: number;
}

interface AddAnotherLineItemButtonProps {
  onClick: () => void;
}

function AddAnotherLineItemButton_(props: AddAnotherLineItemButtonProps) {
  return (
    <Button size="link" icon={PlusCircleIcon} onClick={props.onClick}>
      Add more to this shipment
    </Button>
  );
}

function MultipleLineItemsContainer_(props: MultipleLineItemsContainerProps) {
  const {
    lineItems,
    forceValidation,
    errorMessages,
    overallErrorMessage,
    savedCommodities,
    onRearrangeLengthWidth,
  } = props;

  const [units, setUnits] = useState<Units>(Units.Imperial);
  const [pickerOpen, setPickerOpen] = useState(false);

  // At the cap every way of adding a card goes: the add button, the saved
  // commodity picker, the "add another row" links (they hide when there is no
  // add handler) and Duplicate. Stopping here rather than letting the customer
  // add past it and then rejecting the quote is safe because extra freight
  // fits into an existing line's quantity, which the note tells them.
  const atLineItemLimit = lineItems.length >= props.maxLineItems;
  const addLineItem = atLineItemLimit ? undefined : props.addAnotherLineItem;

  if (lineItems.length === 0 && savedCommodities.length > 0) {
    return (
      <Stack gap={4}>
        <BookShipmentSelectInitialSavedCommodity
          savedCommodities={savedCommodities}
          onSomethingElse={props.addAnotherLineItem}
          onSelectSavedCommodity={props.addLineItemFromSavedCommodity}
        />
        {overallErrorMessage && forceValidation && (
          <Row gap={2} justify="start">
            <Icon icon={WarningCircleIcon} weight="fill" color="text-red" />
            <T color="red">Please select a commodity to add to the shipment</T>
          </Row>
        )}
      </Stack>
    );
  }

  return (
    // The Stack's gap replaces the per-item spacers that used to separate the
    // line-item cards and the add button.
    <Stack gap={6}>
      {lineItems.map((lineItem, index) => {
        const multipleLineItems = lineItems.length > 1;
        const lineItemsErrorMessages = errorMessages.get(lineItem.lineItemId);
        if (lineItemsErrorMessages === undefined) {
          throw new Error("Missing error messages for lineItem");
        }

        return (
          <div key={lineItem.lineItemId}>
            <LineItemsContainer
              badge={undefined}
              index={index + 1}
              numberLineItems={lineItems.length}
              lineItem={lineItem}
              onChange={(value: Patch<LineItem>) => {
                props.onUpdateLineItem(lineItem.lineItemId, value);
              }}
              onRearrangeLengthWidth={() =>
                onRearrangeLengthWidth(lineItem.lineItemId)
              }
              showRemoveLineItemButton={multipleLineItems}
              onAddLineItem={addLineItem}
              onDuplicateLineItem={() => {
                props.duplicateLineItem(lineItem.lineItemId);
              }}
              duplicateDisabledMessage={
                atLineItemLimit
                  ? `Shipments can have up to ${props.maxLineItems} line items`
                  : undefined
              }
              onRemoveLineItem={() => {
                props.removeLineItemById(lineItem.lineItemId);
              }}
              lineItemErrorMessages={lineItemsErrorMessages}
              forceValidation={forceValidation}
              shouldRenderFreightClass={props.shouldRenderFreightClass}
              setHasNmfcError={function (hasNmfcError: boolean) {
                props.setHasNmfcError(lineItem.lineItemId, hasNmfcError);
              }}
              units={units}
              setUnits={setUnits}
              pickupLocation={props.pickupLocation}
              deliveryLocation={props.deliveryLocation}
              protectFromFreezingOutOfSeason={
                props.protectFromFreezingOutOfSeason
              }
            />
          </div>
        );
      })}
      {/* Only reachable over the cap, which the form itself no longer allows:
          a draft or a requote can arrive that way. Shown at once, not on
          submit, so the customer knows before pressing Get Quotes. */}
      {overallErrorMessage && lineItems.length > 0 && (
        <div data-testid="line-items-overall-error">
          <Row gap={2} justify="start">
            <Icon icon={WarningCircleIcon} weight="fill" color="text-red" />
            <T color="red">{overallErrorMessage}</T>
          </Row>
        </div>
      )}
      {/* With saved commodities the button opens the same inline picker panel
          the first line item gets, rather than a floating one — one pattern for
          "what would you like to ship?", wherever you are in the list. With
          none saved there is nothing to pick from, so it just adds a row. */}
      {atLineItemLimit ? (
        <LineItemsLimitReachedNote maxLineItems={props.maxLineItems} />
      ) : pickerOpen ? (
        <BookShipmentSelectInitialSavedCommodity
          savedCommodities={savedCommodities}
          onSelectSavedCommodity={(savedCommodity) => {
            setPickerOpen(false);
            props.addLineItemFromSavedCommodity(savedCommodity);
          }}
          onSomethingElse={() => {
            setPickerOpen(false);
            props.addAnotherLineItem();
          }}
        />
      ) : (
        <Stack align="center">
          <AddAnotherLineItemButton_
            onClick={
              savedCommodities.length === 0
                ? props.addAnotherLineItem
                : () => setPickerOpen(true)
            }
          />
        </Stack>
      )}
    </Stack>
  );
}

interface WeightSummaryProps {
  lineItems: AddLineItemType[];
}

function WeightSummary_(props: WeightSummaryProps) {
  const totalWeight = totalWeightForLineItems(props.lineItems);
  if (totalWeight === undefined) {
    return <></>;
  }

  if (props.lineItems.length < 2) {
    return <></>;
  }

  return <T size="sm">Total Weight: {totalWeight.toLocaleString()}lb</T>;
}
interface ShipmentContentsSection2Props {
  lineItems: Array<AddLineItemType>;
  forceValidation: boolean;
  errorMessageForLineItems: Map<string, LineItemErrors>;
  overallErrorMessage: ErrorMessageType;
  shouldRenderFreightClass: boolean;
  index: number;
  onUpdateLineItem: (lineItemId: UUID, value: Patch<LineItem>) => void;
  addAnotherLineItem: () => void;
  duplicateLineItem: (lineItemId: UUID) => void;
  removeLineItemById: (lineItemId: UUID) => void;
  setHasNmfcError: (lineItemId: UUID, hasNmfcError: boolean) => void;
  savedCommodities: Array<SavedCommodity>;
  addLineItemFromSavedCommodity: (_: SavedCommodity) => void;
  onRearrangeLengthWidth: (lineItemId: UUID) => void;
  pickupLocation: AddLocationLocationType;
  deliveryLocation: AddLocationLocationType;
  protectFromFreezingOutOfSeason: boolean;
  maxLineItems: number;
}
function BookShipmentContentsSection2(props: ShipmentContentsSection2Props) {
  return (
    <BookShipmentSection
      index={props.index}
      width="wider"
      header="Shipment Contents"
      description={
        <Stack gap={2} align="center">
          {/* The bubble stays in the sentence's inline flow and brings its own
              left margin — no flex row to line it up. */}
          <div>
            Please be accurate with the details you enter below
            <BookShipmentAdditionalChargesQuestionBubble />
          </div>
          <WeightSummary_ lineItems={props.lineItems} />
        </Stack>
      }
      id="shipment-contents"
    >
      <MultipleLineItemsContainer_
        lineItems={props.lineItems}
        forceValidation={props.forceValidation}
        errorMessages={props.errorMessageForLineItems}
        overallErrorMessage={props.overallErrorMessage}
        onUpdateLineItem={props.onUpdateLineItem}
        addAnotherLineItem={props.addAnotherLineItem}
        duplicateLineItem={props.duplicateLineItem}
        removeLineItemById={props.removeLineItemById}
        shouldRenderFreightClass={props.shouldRenderFreightClass}
        setHasNmfcError={props.setHasNmfcError}
        savedCommodities={props.savedCommodities}
        addLineItemFromSavedCommodity={props.addLineItemFromSavedCommodity}
        onRearrangeLengthWidth={props.onRearrangeLengthWidth}
        pickupLocation={props.pickupLocation}
        deliveryLocation={props.deliveryLocation}
        protectFromFreezingOutOfSeason={props.protectFromFreezingOutOfSeason}
        maxLineItems={props.maxLineItems}
      />
    </BookShipmentSection>
  );
}
export default BookShipmentContentsSection2;
